import ControleReceitasModal from 'components/receita/ControleReceitasModal';
import { useState } from 'react';
import { Badge, Button, Card, Table } from 'react-bootstrap';
import { FaEdit, FaFileInvoice, FaListAlt, FaLock } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import history from 'services/history';
import { resolveUsuarioAgenda } from 'utils/agenda';

const PACIENTES_BLOQUEADOS = [1, 20295, 18262, 12599, 90308]; // isso não deve ser enforced no backend???

export default function TabelaPaciente({
  showTablePaciente,
  pacientes,
  onEditar,
  onSelectPaciente,
  pacientesSearchRestore = null,
}) {
  const func = useSelector(selectFunc);
  const doctor = useSelector(selectDoctor);
  const { codFunc, nome: nomeFunc } = resolveUsuarioAgenda({ func, doctor });
  const [pacienteReceitas, setPacienteReceitas] = useState(null);

  const tabelaVisivel = showTablePaciente && pacientes?.length > 0;

  const handleTentarEditar = (pac) => {
    const codPac = Number(pac.CODPAC);
    if (PACIENTES_BLOQUEADOS.includes(codPac)) {
      toast.warn('Paciente protegido! Este registro não pode ser editado.');
      return;
    }
    onEditar(pac);
  };

  const handleIrParaAtividades = (pac, e) => {
    e?.stopPropagation();

    const params = new URLSearchParams({
      codpac: pac.CODPAC,
      nomePaciente: pac.NOMEPAC || '',
      ...(codFunc ? { codFunc } : {}),
      ...(nomeFunc ? { nomeFunc } : {}),
    });

    if (pacientesSearchRestore) {
      history.replace('/pacientes', {
        pacientesSearch: { ...pacientesSearchRestore, fromAtividades: true },
      });
    }

    history.push(`/atividades?${params.toString()}`, {
      nome: pac.NOMEPAC,
      ecg: false,
    });
  };

  const handleAbrirReceitas = (pac, e) => {
    e?.stopPropagation();
    setPacienteReceitas(pac);
  };

  return (
    <>
      {tabelaVisivel && (
        <div className="mt-3">
          {/* LAYOUT MOBILE (DISPOSITIVOS < 768px): CARDS EMPILHADOS   */}
          <div className="d-md-none d-flex flex-column gap-2">
            {pacientes.map((pac) => {
              const isBloqueado = PACIENTES_BLOQUEADOS.includes(Number(pac.CODPAC));

              return (
                <Card
                  key={pac.CODPAC}
                  className="shadow-sm border-start border-4 border-primary"
                  onClick={() => handleTentarEditar(pac)}
                  style={{ cursor: isBloqueado ? 'not-allowed' : 'pointer' }}
                >
                  <Card.Body className="p-3">
                    {onSelectPaciente && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mb-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectPaciente(pac);
                        }}
                      >
                        Agendar
                      </Button>
                    )}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="fw-bold mb-0 text-primary">{pac.NOMEPAC}</h6>
                        <small className="text-muted">Cód: {pac.CODPAC}</small>
                      </div>
                      {isBloqueado && (
                        <Badge bg="danger" className="d-flex align-items-center gap-1">
                          <FaLock size={10} /> Protegido
                        </Badge>
                      )}
                    </div>

                    <div className="d-flex justify-content-between text-muted fs-7 mb-3 bg-light p-2 rounded">
                      <div>
                        <strong className="d-block text-dark">Nascimento</strong>
                        <span>{pac.dtNasc || '-'}</span>
                      </div>
                      <div>
                        <strong className="d-block text-dark">CPF</strong>
                        <span>{pac.CPFPAC || '-'}</span>
                      </div>
                      <div>
                        <strong className="d-block text-dark">Celular</strong>
                        <span>{pac.CELULARPAC || '-'}</span>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1"
                        onClick={(e) => handleIrParaAtividades(pac, e)}
                      >
                        <FaListAlt /> Atividades
                      </Button>

                      <Button
                        variant="outline-info"
                        size="sm"
                        className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1"
                        onClick={(e) => handleAbrirReceitas(pac, e)}
                      >
                        <FaFileInvoice /> Receitas
                      </Button>

                      <Button
                        variant={isBloqueado ? 'outline-secondary' : 'primary'}
                        size="sm"
                        className="flex-fill d-flex align-items-center justify-content-center gap-1 py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTentarEditar(pac);
                        }}
                      >
                        <FaEdit /> Editar
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {/* LAYOUT DESKTOP (DISPOSITIVOS ≥ 768px): TABELA PADRÃO    */}
          <div className="d-none d-md-block">
            <Table responsive bordered hover size="sm" className="align-middle shadow-sm">
              <thead className="table-primary text-center">
                <tr>
                  <th>Código</th>
                  <th className="text-start">Nome do Paciente</th>
                  <th>Dt Nasc</th>
                  <th>CPF</th>
                  <th>Celular</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pacientes.map((pac) => {
                  const isBloqueado = PACIENTES_BLOQUEADOS.includes(Number(pac.CODPAC));

                  return (
                    <tr
                      key={pac.CODPAC}
                      onDoubleClick={() => handleTentarEditar(pac)}
                      style={{ cursor: isBloqueado ? 'not-allowed' : 'pointer' }}
                      title={
                        isBloqueado ? 'Paciente bloqueado para edição' : 'Clique duplo para editar'
                      }
                    >
                      <td className="text-center fw-semibold text-secondary">{pac.CODPAC}</td>

                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {pac.NOMEPAC}
                          {isBloqueado && (
                            <Badge bg="danger" style={{ fontSize: '0.65rem' }}>
                              Protegido
                            </Badge>
                          )}
                        </div>
                      </td>

                      <td className="text-center">{pac.dtNasc || '-'}</td>
                      <td className="text-center text-nowrap">{pac.CPFPAC || '-'}</td>
                      <td className="text-center text-nowrap">{pac.CELULARPAC || '-'}</td>

                      <td>
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={(e) => handleIrParaAtividades(pac, e)}
                            className="rounded-pill px-2 py-0"
                            title="Ver atividades do paciente"
                          >
                            Atividades
                          </Button>

                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={(e) => handleAbrirReceitas(pac, e)}
                            className="rounded-pill px-2 py-0"
                            title="Ver receitas do paciente"
                          >
                            Controle
                          </Button>

                          <Button
                            variant={isBloqueado ? 'outline-secondary' : 'outline-primary'}
                            size="sm"
                            className="rounded-pill px-2 py-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTentarEditar(pac);
                            }}
                            title={
                              isBloqueado ? 'Edição não permitida' : 'Editar dados do paciente'
                            }
                          >
                            Editar
                          </Button>
                          {onSelectPaciente && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="rounded-pill px-2 py-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectPaciente(pac);
                              }}
                              title="Selecionar paciente para agenda"
                            >
                              Agendar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      <ControleReceitasModal
        show={!!pacienteReceitas}
        paciente={pacienteReceitas}
        onHide={() => setPacienteReceitas(null)}
      />
    </>
  );
}
